import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';

import { HttpParams } from '@angular/common/http';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { ApplicationApiClient, isNkHttpError } from '@nk/api-client-lib';
import { AppOnboardPreflightResponse, AppUserAccountEntity, Auth0UserVerified } from '@nk/data-entities-lib';
import { EmailValidators, NkToastrService } from '@nk/ui-lib';
import { map } from 'rxjs/operators';

class OnboardingUserForm {
  constructor(
    public first_name: string,
    public last_name: string,
    public password: string,
    public role_type_key: string,
    public marketing_agreed: boolean,
  ) {}
}
/**
 * @company_name just for sign-up existing page
 */
class OnboardingCompanyForm {
  constructor(
    // public org_nr_schema: string, extrapolate from company uuid.
    // public org_nr: string, extrapolate from company uuid.
    // public company_short_name: string, extrapolate from company uuid.
    public company_uuid: string,
    public company_name: string,
    public phone_number: string,
    public email: string,
    public industry_type_key: string,
  ) {}
}
class ContractAgreementForm {
  constructor(
    public tos_agreed: boolean,
    public privacy_agreed: boolean,
    public privacy_content_hash_uuid: string,
    public tos_content_hash_uuid: string,
  ) {}
}

export interface AppOnboard {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  role_type_key: string;
  company_uuid: string;
  phone_number: string;
  industry_type_key: string;
  marketing_agreed: boolean;
  tos_agreed: boolean;
  privacy_agreed: boolean;
  app_plan_key: string;
}

/**
 * Default trial period in days, should correspond default_grace_period_days for app_trial feature package.
 */
export const DEFAULT_TRIAL_PERIOD = 14;

export function validOnboardingEmail(
  signUpService: SignUpService,
): (input: FormControl) => Observable<ValidationErrors> {
  return (input: FormControl) =>
    signUpService.checkOnboardingPreflight(input['value'], null).pipe(
      map((payload) => {
        if (payload.malformed_email_flag) {
          return {
            malformed_email_flag: true,
          };
        } else if (payload.duplicate_email_flag === true) {
          return {
            emailTaken: true,
          };
        } else {
          return null;
        }
      }),
    );
}

@Injectable()
export class SignUpService {
  private _showErrorsObs$ = new Subject<boolean>();
  public signedUpUserAccount: AppUserAccountEntity;
  public secretUuid: string;
  public allowSelfServiceSignup = false;

  public roleTypeKeyList = [
    // 'general',
    'credit_risk',
    'compliance',
    'sales',
    'investing',
  ];
  public industryTypeKeyList = ['banking_and_finance', 'investment', 'professional_services', 'public', 'other'];
  // find out if we need to add this to a service also..
  public onboardingUserForm = new OnboardingUserForm('', '', '', this.roleTypeKeyList[0], false);
  public onboardingCompanyForm = new OnboardingCompanyForm(null, null, null, null, this.industryTypeKeyList[0]);
  public contractAgreementForm = new ContractAgreementForm(false, false, null, null);

  // !important Validator for password are added by sub component
  public formUser = this._formBuilder.group<Record<keyof OnboardingUserForm, unknown[]>>({
    first_name: [this.onboardingUserForm.first_name, [Validators.required, Validators.maxLength(255)]],
    last_name: [this.onboardingUserForm.last_name, [Validators.required, Validators.maxLength(255)]],
    role_type_key: [undefined, [Validators.required]],
    marketing_agreed: [this.onboardingUserForm.marketing_agreed, [Validators.required]],
    password: [this.onboardingUserForm.password, [Validators.required]],
  });

  public formCustomer = this._formBuilder.group<Record<keyof OnboardingCompanyForm, unknown[]>>({
    company_uuid: [this.onboardingCompanyForm.company_uuid, [Validators.required]],
    company_name: [this.onboardingCompanyForm.company_uuid],
    phone_number: [this.onboardingCompanyForm.phone_number, [Validators.required, Validators.minLength(5)]],
    email: [
      this.onboardingCompanyForm.email,
      [Validators.required, Validators.minLength(3), EmailValidators.emailFormatValidator(), Validators.email],
      validOnboardingEmail(this),
    ],
    industry_type_key: [undefined, [Validators.required]],
  });

  public formContractAgreement = this._formBuilder.group<Record<keyof ContractAgreementForm, unknown[]>>({
    tos_agreed: [this.contractAgreementForm.tos_agreed, [Validators.required, Validators.requiredTrue]],
    privacy_agreed: [this.contractAgreementForm.privacy_agreed, [Validators.required, Validators.requiredTrue]],
    privacy_content_hash_uuid: [this.contractAgreementForm.privacy_content_hash_uuid, [Validators.required]],
    tos_content_hash_uuid: [this.contractAgreementForm.tos_content_hash_uuid, [Validators.required]],
  });

  public planKey = 'app_pro';
  // List is used to advance in wizard by changing route.
  public stageList = [
    {
      path: 'company',
      order: 0,
      label: extractTranslation('sign_up.stage.company_account_information'),
      form: this.formCustomer,
      showErrors: false,
    },
    {
      path: 'account',
      order: 1,
      label: extractTranslation('sign_up.stage.personal_details'),
      form: this.formUser,
      showErrors: false,
    },
    {
      path: 'tos-privacy',
      order: 2,
      label: extractTranslation('sign_up.stage.terms_of_service_and_privacy_policy'),
      form: this.formContractAgreement,
      showErrors: false,
    },
  ];
  public currentStage = 0;
  public params: Params;
  public pathPrefix = 'sign-up';
  public getShowError(): Observable<boolean> {
    return this._showErrorsObs$.asObservable();
  }
  public setShowError(data: boolean): void {
    this._showErrorsObs$.next(data);
  }

  constructor(
    private _api: ApplicationApiClient,
    private _formBuilder: FormBuilder,
    private _router: Router,
    private _toast: NkToastrService,
  ) {}

  public postSignupApp(): void {}

  public completeSignup(): void {
    // TODO: Parse phone number to a number.
    const onboardingPayload = {
      first_name: this.formUser.value.first_name,
      last_name: this.formUser.value.last_name,
      email: this.formCustomer.value.email,
      password: this.formUser.value.password,
      role_type_key: this.formUser.value.role_type_key,
      company_uuid: this.formCustomer.value.company_uuid,
      phone_number: this.formCustomer.value.phone_number,
      industry_type_key: this.formCustomer.value.industry_type_key,
      marketing_agreed: this.formUser.value.marketing_agreed,
      tos_agreed: this.formContractAgreement.value.tos_agreed,
      privacy_agreed: this.formContractAgreement.value.privacy_agreed,
      tos_content_hash_uuid: this.formContractAgreement.value.tos_content_hash_uuid,
      privacy_content_hash_uuid: this.formContractAgreement.value.privacy_content_hash_uuid,
      app_plan_key: this.planKey,
    };
    if (this.secretUuid) {
      onboardingPayload['secret_uuid'] = this.secretUuid;
    }
    this.postAppOnboard(onboardingPayload).subscribe(
      (payload) => {
        this.signedUpUserAccount = payload;
        this._router.navigate(['/sign-up-success']);
      },
      (error: unknown) => {
        if (isNkHttpError(error)) {
          this._toast.error(error.error.message, `${error.status}: We encountered an error`);
        } else {
          this._toast.error('We encountered an error');
        }
      },
    );
  }

  public postAppOnboard(payload: Partial<AppUserAccountEntity>): Observable<AppUserAccountEntity> {
    return this._api.post<AppUserAccountEntity>('app-onboard', payload);
  }

  public getAuth0UserFromAppUserAccount(appUserAccountUuid: string): Observable<Auth0UserVerified> {
    return this._api.get<Auth0UserVerified>(`/app-user-account/${appUserAccountUuid}/auth0-user`);
  }

  public checkOnboardingPreflight(
    email?: string,
    companyUuid?: string,
    secretUuid?: string,
  ): Observable<AppOnboardPreflightResponse> {
    let params = new HttpParams();
    if (email) {
      params = params.append('email', email);
    }
    if (companyUuid) {
      params = params.append('company_uuid', companyUuid);
    }
    if (secretUuid) {
      params = params.append('secret_uuid', secretUuid);
    }
    return this._api.get<AppOnboardPreflightResponse>('/app-onboard-preflight', { params: params }, null);
  }

  public postOnboardingExistingCompany(payload: {
    company_uuid: string;
    phone_number: string;
    email: string;
  }): Observable<unknown> {
    return this._api.post<unknown>('/app-onboard-existing-company', payload, null);
  }
}
